'use client';

import { useTranslations } from 'next-intl';
import { motion, useTransform, type MotionValue } from 'motion/react';
import { useEffect, useState } from 'react';
import { customRichTextTags } from '@/utils/i18n';
import { ReanalysisLink } from './reanalysis-content';
import {
  CONCLUSION_SETTLED,
  LINK_VISIBLE,
  SCENE_START,
  SUMMARY_TYPING,
} from './reanalysis-timeline';

// 1920×1080 고정 캔버스를 축소해 그리므로 뷰포트별 줄바꿈 분기가 없다 — 항상 줄바꿈하는 <br>
const richTags = { br: customRichTextTags.solidBr };

export function SummaryTypingText({
  progress,
}: {
  progress: MotionValue<number>;
}) {
  const t = useTranslations('Main.reanalysis');
  const lines = t.raw('titleLines') as string[];
  const total = lines.reduce((sum, line) => sum + line.length, 0);
  const [count, setCount] = useState(0);
  // 스크롤 이벤트마다 리렌더하지 않도록 프레임당 한 번만 글자 수를 갱신한다.
  useEffect(() => {
    let frame: number | undefined;
    const { start, end } = SUMMARY_TYPING;
    const apply = () => {
      frame = undefined;
      const ratio = (progress.get() - start) / (end - start);
      setCount(Math.floor(Math.min(1, Math.max(0, ratio)) * total));
    };
    const schedule = () => {
      if (frame === undefined) frame = requestAnimationFrame(apply);
    };
    apply();
    const unsubscribe = progress.on('change', schedule);
    return () => {
      unsubscribe();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [progress, total]);
  let offset = 0;
  return lines.map((line, index) => {
    const start = offset;
    offset += line.length;
    const revealed = Math.min(Math.max(count - start, 0), line.length);
    const caret = count >= start && count < offset;
    return (
      <span key={index} className="block" data-typed-count={revealed}>
        <span className="relative inline-block whitespace-pre align-top">
          <span className="text-white/20">{line}</span>
          <span
            aria-hidden="true"
            className="absolute left-0 top-0 whitespace-pre text-white"
          >
            {line.slice(0, revealed)}
            {caret && (
              <motion.span
                className="absolute top-[3px] ml-[2px] inline-block h-[24px] w-[2px] rounded-[20px] bg-[#4d85ff] xl:top-[6px] xl:h-[60px] xl:w-[6px]"
                animate={{ opacity: [1, 1, 0, 0] }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
              />
            )}
          </span>
        </span>
      </span>
    );
  });
}

export default function ReanalysisSummary({
  progress,
  visible,
  showLink,
}: {
  progress: MotionValue<number>;
  visible: boolean;
  showLink: boolean;
}) {
  const t = useTranslations('Main.reanalysis');
  const summaryOpacity = useTransform(
    progress,
    [0.73, SUMMARY_TYPING.start],
    [0, 1],
  );
  const titleY = useTransform(
    progress,
    [SCENE_START.conclusion, CONCLUSION_SETTLED],
    [151.5, 0],
  );
  const quoteOpacity = useTransform(progress, [0.9, LINK_VISIBLE], [0, 1]);
  const buttonOpacity = useTransform(progress, [LINK_VISIBLE, 0.97], [0, 1]);
  return (
    <motion.div
      className="absolute left-[581.5px] top-[280.5px] flex w-[757px] flex-col items-center gap-10 text-center text-white"
      style={{ opacity: summaryOpacity }}
      aria-hidden={!visible}
    >
      <motion.h3
        className="relative w-full text-[60px] font-semibold leading-[72px] tracking-[-1.344px]"
        style={{ y: titleY }}
      >
        <SummaryTypingText progress={progress} />
      </motion.h3>
      <motion.blockquote
        className="break-keep text-[24px] font-medium leading-[38px] tracking-[-0.464px] text-white/80"
        style={{ opacity: quoteOpacity }}
      >
        <p className="whitespace-nowrap">{t.rich('quote', richTags)}</p>
        <footer className="mt-[38px]">{t('author')}</footer>
      </motion.blockquote>
      <motion.div
        style={{ opacity: buttonOpacity }}
        inert={!showLink}
        aria-hidden={!showLink}
      >
        <ReanalysisLink />
      </motion.div>
    </motion.div>
  );
}
